import {useCallback, useMemo, useState} from 'react';

import {
  ActionFilterCriteria,
  AuditLogReportType,
  AuditLogsDataResponse,
  getAuditLogsData,
} from 'audit/AuditDAO';
import useOnChangeOnly from 'ui-library/hooks/useOnChangeOnly';
import useOnMount from 'ui-library/hooks/useOnMount';

interface UseAuditLogDataProps {
  ids: Array<string>;
  pageSize: number;
  resultsWarehouseIds?: Array<string>;
  relatedResultsModelId?: string;
  userIds?: Array<string>;
  actions?: Array<ActionFilterCriteria>;
  reasonCodes?: Array<string>;
  startDate?: string;
  endDate?: string;
  sortAscending?: boolean;
  reportType?: AuditLogReportType;
}

interface AuditLogDataState {
  isLoading: boolean;
  auditData: AuditLogsDataResponse | null;
  currentPage: number;
  error: string | null;
}

interface UseAuditLogDataResult {
  state: AuditLogDataState;
  allColumns: Array<string>;
  loadAuditData: (page?: number) => void;
}

export default function useAuditLogData({
  ids,
  pageSize,
  resultsWarehouseIds,
  relatedResultsModelId,
  userIds,
  actions,
  reasonCodes,
  startDate,
  endDate,
  sortAscending,
  reportType,
}: UseAuditLogDataProps): UseAuditLogDataResult {
  const [state, setState] = useState<AuditLogDataState>({
    isLoading: true,
    auditData: null,
    currentPage: 0,
    error: null,
  });
  const allColumns = useMemo(() => state.auditData?.columns || [], [state.auditData]);

  const loadAuditData = useCallback(
    async (page: number = 0) => {
      setState((prev) => ({...prev, isLoading: true, error: null}));

      try {
        const response: AuditLogsDataResponse = await getAuditLogsData(ids, {
          resultsWarehouseIds,
          relatedResultsModelId,
          pageSize,
          pageOffset: page,
          userIds: userIds && userIds.length > 0 ? userIds : undefined,
          actions: actions && actions.length > 0 ? actions : undefined,
          reasonCodes: reasonCodes && reasonCodes.length > 0 ? reasonCodes : undefined,
          startDate,
          endDate,
          sortAscending,
          reportType,
        });

        setState((prev) => ({
          ...prev,
          isLoading: false,
          auditData: response,
          currentPage: page,
        }));
        // eslint-disable-next-line benchlint/always-throw-from-catch
      } catch {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: 'Failed to load audit log data. Please try again.',
        }));
      }
    },
    [
      ids,
      pageSize,
      resultsWarehouseIds,
      relatedResultsModelId,
      userIds,
      actions,
      reasonCodes,
      startDate,
      endDate,
      sortAscending,
      reportType,
    ]
  );

  // Initial load
  useOnMount(() => {
    loadAuditData(0);
  });

  // Re-fetch from page 0 when filters change. useOnChangeOnly skips the initial mount
  // since useOnMount already handles the first load.
  useOnChangeOnly(() => {
    loadAuditData(0);
  }, [userIds, actions, reasonCodes, startDate, endDate, sortAscending, reportType]);

  return {
    state,
    allColumns,
    loadAuditData,
  };
}
